import React, { useState } from 'react';
import { Download, CheckCircle, Clock, TrendingUp, Award, ChevronDown, ChevronUp, FileText } from 'lucide-react';
import { trackPDFTeaserView } from '../utils/analytics';
import { PDFSelectionModal } from './PDFSelectionModal';

interface PDFResource {
  title: string;
  url: string;
}

interface PDFTeaserProps {
  moduleId: number;
  pdfFilename: string;
  pdfResources?: PDFResource[]; // Optional: for modules with multiple PDFs
  title: string;
  subtitle: string;
  description: string;
  urgency?: {
    text: string;
    bonus?: string;
  };
  teasers: {
    title: string;
    items: string[];
  };
  socialProof?: {
    testimonial?: string;
    author?: string;
    company?: string;
    roiData?: string;
  };
  ctaText: string;
  onDownload: (filename: string) => void;
  onDownloadMultiple?: (filenames: string[]) => void; // For multiple PDF downloads
  userId?: string;
  userTier?: string;
}

export function PDFTeaser({
  moduleId,
  pdfFilename,
  pdfResources,
  title,
  subtitle,
  description,
  urgency,
  teasers,
  socialProof,
  ctaText,
  onDownload,
  onDownloadMultiple,
  userId,
  userTier
}: PDFTeaserProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showPDFModal, setShowPDFModal] = useState(false);

  React.useEffect(() => {
    // Track when teaser is viewed
    trackPDFTeaserView({ moduleId, userId });
  }, [moduleId, userId]);

  const handleDownload = () => {
    // If multiple PDFs available and onDownloadMultiple is provided, show modal
    if (pdfResources && pdfResources.length > 1 && onDownloadMultiple) {
      setShowPDFModal(true);
    } else {
      // Single PDF download
      onDownload(pdfFilename);
    }
  };

  const handleDownloadMultiple = (filenames: string[]) => {
    if (onDownloadMultiple) {
      onDownloadMultiple(filenames);
    } else {
      // Fallback: download each file individually
      filenames.forEach(filename => {
        onDownload(filename);
        // Small delay between downloads to avoid browser blocking
        setTimeout(() => {}, 500);
      });
    }
  };

  return (
    <div className="pdf-teaser-card" style={{
      background: 'white',
      borderRadius: '16px',
      padding: '2rem',
      marginBottom: '2rem',
      border: '1px solid #e5e7eb',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
    }}>
      {/* Header */}
      <div style={{ marginBottom: '1.5rem' }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          marginBottom: '1rem'
        }}>
          <Award style={{ color: 'var(--orange)', width: '24px', height: '24px' }} />
          <h2 style={{
            margin: 0,
            fontSize: '1.75rem',
            fontWeight: '700',
            color: 'var(--dark-gray)'
          }}>
            {title}
          </h2>
        </div>
        <p style={{
          margin: 0,
          fontSize: '1.1rem',
          fontWeight: '600',
          color: 'var(--orange)',
          marginBottom: '1rem'
        }}>
          {subtitle}
        </p>
        <p style={{
          margin: 0,
          fontSize: '1rem',
          color: 'var(--gray)',
          lineHeight: '1.6'
        }}>
          {description}
        </p>
      </div>

      {/* Urgency Banner */}
      {urgency && (
        <div style={{
          background: 'linear-gradient(135deg, rgba(216, 183, 37, 0.1) 0%, rgba(4, 39, 137, 0.1) 100%)',
          borderRadius: '8px',
          padding: '1rem',
          marginBottom: '1.5rem',
          border: '1px solid rgba(216, 183, 37, 0.2)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
            <Clock style={{ color: 'var(--orange)', width: '20px', height: '20px' }} />
            <strong style={{ color: 'var(--dark-gray)' }}>{urgency.text}</strong>
          </div>
          {urgency.bonus && (
            <p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--gray)' }}>
              {urgency.bonus}
            </p>
          )}
        </div>
      )}

      {/* Social Proof */}
      {socialProof && (
        <div style={{
          background: 'white',
          borderRadius: '8px',
          padding: '1rem',
          marginBottom: '1.5rem',
          border: '1px solid #e5e7eb'
        }}>
          {socialProof.testimonial && (
            <div style={{ marginBottom: '0.75rem' }}>
              <p style={{
                margin: 0,
                fontSize: '0.95rem',
                fontStyle: 'italic',
                color: 'var(--gray)',
                lineHeight: '1.6'
              }}>
                "{socialProof.testimonial}"
              </p>
              {socialProof.author && (
                <p style={{
                  margin: '0.5rem 0 0 0',
                  fontSize: '0.85rem',
                  fontWeight: '600',
                  color: 'var(--dark-gray)'
                }}>
                  — {socialProof.author}
                  {socialProof.company && `, ${socialProof.company}`}
                </p>
              )}
            </div>
          )}
          {socialProof.roiData && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              paddingTop: socialProof.testimonial ? '0.75rem' : 0,
              borderTop: socialProof.testimonial ? '1px solid #e5e7eb' : 'none'
            }}>
              <TrendingUp style={{ color: 'var(--orange)', width: '18px', height: '18px' }} />
              <p style={{
                margin: 0,
                fontSize: '0.9rem',
                color: 'var(--dark-gray)',
                fontWeight: '500'
              }}>
                {socialProof.roiData}
              </p>
            </div>
          )}
        </div>
      )}

      {/* What's Inside Teaser */}
      <div style={{
        background: 'white',
        borderRadius: '8px',
        padding: '1rem',
        marginBottom: '1.5rem',
        border: '1px solid #e5e7eb'
      }}>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: 'none',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
            textAlign: 'left'
          }}
        >
          <h3 style={{
            margin: 0,
            fontSize: '1.1rem',
            fontWeight: '600',
            color: 'var(--dark-gray)',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            textDecoration: 'none'
          }}>
            <FileText className="pdf-teaser-doc-icon" style={{ width: '20px', height: '20px', color: 'var(--orange)', flexShrink: 0 }} />
            <span style={{ color: 'var(--dark-gray)' }}>{teasers.title}</span>
          </h3>
          {isExpanded ? (
            <ChevronUp style={{ color: 'var(--gray)', width: '20px', height: '20px' }} />
          ) : (
            <ChevronDown style={{ color: 'var(--gray)', width: '20px', height: '20px' }} />
          )}
        </button>
        
        {isExpanded && (
          <ul style={{
            margin: '1rem 0 0 0',
            paddingLeft: '1.5rem',
            listStyle: 'none'
          }}>
            {teasers.items.map((item, index) => (
              <li key={index} style={{
                marginBottom: '0.75rem',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '0.5rem'
              }}>
                <CheckCircle style={{
                  color: 'var(--orange)',
                  width: '18px',
                  height: '18px',
                  flexShrink: 0,
                  marginTop: '2px'
                }} />
                <span style={{
                  fontSize: '0.95rem',
                  color: 'var(--gray)',
                  lineHeight: '1.5'
                }}>
                  {item}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* CTA Button */}
      <button
        onClick={handleDownload}
        style={{
          width: '100%',
          background: 'var(--orange)',
          color: 'white',
          border: 'none',
          borderRadius: '8px',
          padding: '1rem 2rem',
          fontSize: '1.1rem',
          fontWeight: '700',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '0.75rem',
          transition: 'all 0.2s ease',
          boxShadow: '0 4px 12px rgba(255, 152, 0, 0.3)'
        }}
        onMouseOver={(e) => {
          e.currentTarget.style.background = '#e68900';
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = '0 6px 16px rgba(255, 152, 0, 0.4)';
        }}
        onMouseOut={(e) => {
          e.currentTarget.style.background = 'var(--orange)';
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 4px 12px rgba(255, 152, 0, 0.3)';
        }}
      >
        <Download className="pdf-teaser-download-icon" style={{ width: '24px', height: '24px', flexShrink: 0 }} />
        {ctaText}
      </button>

      {/* PDF Selection Modal for multiple PDFs */}
      {pdfResources && pdfResources.length > 1 && (
        <PDFSelectionModal
          isOpen={showPDFModal}
          onClose={() => setShowPDFModal(false)}
          pdfs={pdfResources}
          onDownload={handleDownloadMultiple}
        />
      )}
    </div>
  );
}

